import { useCallback } from 'react';

import { useToast } from '@chakra-ui/react';

import { identifyEvent } from '~/features/analytics/identify-event';
import { trackEvent } from '~/features/analytics/track-event';
import { pushDataLayer } from '~/features/analytics/utils/data-layer';
import { getAnonymousIdCookie } from '~/features/cookies/utils/get-anonymous-id-cookie';
import { useUser } from '~/features/user/hooks/use-user';
import { hashEmailClientSide } from '~/utils/email-hash';

const DEFAULT_NEWSLETTER_FORM_ID = 'Newsletter Signup';
const DEFAULT_NEWSLETTER_LEAD_LABEL = 'Lead Generation - HQ - Newsletter Signup';

export function useNewsletterSignUpHandler({
  showToast = true,
  formId = DEFAULT_NEWSLETTER_FORM_ID,
  label = DEFAULT_NEWSLETTER_LEAD_LABEL,
}: { showToast?: boolean; formId?: string; label?: string } = {}) {
  const { user } = useUser();
  const toast = useToast();

  const handleNewsletterSubmit = useCallback(
    async ({ email }: { email: string }) => {
      pushDataLayer({
        event: 'emailSubmitted',
        email_sha256: await hashEmailClientSide(email),
      });

      const userTraits = {
        userIdentifier: user?.id ? user.id : getAnonymousIdCookie()!,
        email,
        ...(user && { name: user.firstName }),
      };

      // Properties that are shared between identify and track events.
      const sharedProperties = { formId };

      if (userTraits.userIdentifier) {
        identifyEvent(
          userTraits.userIdentifier,
          {
            LeadSource: 'Website',
            LeadWebsiteUrl: window.location.href,
            ...sharedProperties,
            ...userTraits,
          },
          { sendToSalesforce: true },
          () => {
            trackEvent({
              type: 'submit',
              label,
              name: 'Marketing Qualified Lead Form Submitted',
              additionalAttributes: {
                gtm_activated: true,
                ...sharedProperties,
                ...userTraits,
              },
            });
          }
        );
      }

      if (showToast) {
        toast({
          title: 'Welcome aboard! 💌',
          description: 'Thank you for joining our newsletter. Get ready for exciting updates!',
          variant: 'success',
        });
      }
    },
    [toast, user, showToast, formId, label]
  );

  return handleNewsletterSubmit;
}
